import { type Content, type ContentType, ContentTypeEnum, type Locale, type Upload } from "@/react/story/types";
import TranslationUtils from "@/react/story/utils/TranslationUtils";

/**
 * A media config value is either a plain URL (legacy/external media) or an Upload object.
 */
const mediaToText = (value: string | Upload | null | undefined): string => {
    if ("string" === typeof value) {
        return value;
    }

    return value?.clientFilename ?? "";
};

export const DEFAULTS = {
    STORY: {
        width: 400,
        height: 800,
    },
    paddingX: 0,
    paddingY: 0,
    angle: 0,
    borderRadius: 0,
    backdropFilter: 0,
    flexBasis: 30,
    fontSize: 100,
    animationDelay: 0,
    animationDuration: 500,
    maxWidth: 50,
    height: 20,
    fontWeight: "normal",
};

export const ARROWS = [
    { value: "flex-start", label: "En haut", icon: "align-top" },
    { value: "center", label: "Centrer", icon: "align-center" },
    { value: "flex-end", label: "En bas", icon: "align-bottom" },
];

export const PADDINGS = [
    { value: 0, label: "Aucune", icon: "ban" },
    { value: 1, label: "XS" },
    { value: 2, label: "SM" },
    { value: 3, label: "MD" },
    { value: 4, label: "LG" },
    { value: 5, label: "XL" },
];

export const TEXT_ALIGNS = [
    { value: "left", label: "À gauche", icon: "text-left" },
    { value: "center", label: "Centrer", icon: "text-center" },
    { value: "right", label: "À droite", icon: "text-right" },
    // {value: 'space-between', label: 'MD'},
];

export const HFLEXS = [
    { value: "flex-start", label: "À gauche", icon: "text-left" },
    { value: "center", label: "Centrer", icon: "text-center" },
    { value: "flex-end", label: "À droite", icon: "text-right" },
    // {value: 'space-between', label: 'MD'},
];

export const VFLEXS = [
    { value: "flex-start", label: "En haut", icon: "align-top" },
    { value: "center", label: "Centrer", icon: "align-center" },
    { value: "flex-end", label: "En bas", icon: "align-bottom" },
    // {value: 'space-between', label: 'MD'},
];

export const CONTENT_TYPES: ContentType[] = [ {
    value: ContentTypeEnum.TITLE,
    label: "Titre",
    icon: "type-h1",
    toText: (content: Content, locale: Locale) => TranslationUtils.get(content.config.text, locale) ?? "",
    placeholder: "Titre",
    field: "text",
}, {
    value: ContentTypeEnum.SUBTITLE,
    label: "Sous-titre",
    icon: "type-h2",
    toText: (content: Content, locale: Locale) => TranslationUtils.get(content.config.text, locale) ?? "",
    placeholder: "Sous-titre",
    field: "text",
}, {
    value: ContentTypeEnum.PARAGRAPH,
    label: "Paragraphe",
    icon: "blockquote-left",
    toText: (content: Content, locale: Locale) => TranslationUtils.get(content.config.text, locale) ?? "",
    placeholder: "Paragraphe",
    field: "text",
}, {
    value: ContentTypeEnum.IMAGE,
    label: "Image",
    icon: "image",
    toText: (content: Content, _: Locale) => mediaToText(content.config.image),
    placeholder: "Image",
    field: "image",
}, {
    value: ContentTypeEnum.VIDEO,
    label: "Vidéo",
    icon: "film",
    toText: (content: Content, _: Locale) => mediaToText(content.config.video ?? content.config.src),
    placeholder: "Vidéo",
    field: "video",
}, {
    value: ContentTypeEnum.AUDIO,
    label: "Audio",
    icon: "volume-up",
    toText: (content: Content, _: Locale) => mediaToText(content.config.src),
    placeholder: "Audio",
    field: "src",
}, {
    value: ContentTypeEnum.ORIGIN,
    label: "Origin'Info",
    icon: "info",
    toText: (content: Content, _: Locale) => content.config.identifier ?? "",
    placeholder: "Identifiant/SKU/EAN",
    field: "identifier",
}, {
    value: ContentTypeEnum.LOCALE_PICKER,
    label: "Sélecteur de langue",
    icon: "globe",
    toText: (content: Content, locale: Locale) => TranslationUtils.get(content.config.text, locale) ?? "",
    placeholder: "Sélecteur de langue",
    field: "text",
} ];

export const FONT_WEIGHTS = [
    { value: "light", label: "Fine (300)" },
    { value: "normal", label: "Normale (400)" },
    { value: "semibold", label: "Élevée (600)" },
    { value: "bold", label: "Très élevée (700)" },
];

export const ANIMATIONS = [
    { value: "", label: "Aucune" },
    { value: "bounceIn", label: "Bounce In" },
    { value: "bounceInRight", label: "Bounce In (droite)" },
    { value: "bounceInLeft", label: "Bounce In (gauche)" },
    { value: "fadeIn", label: "Fade In" },
    { value: "fadeInRight", label: "Fade In (droite)" },
    { value: "fadeInLeft", label: "Fade In (gauche)" },
    { value: "zoomIn", label: "Zoom In" },
    { value: "zoomInRight", label: "Zoom In (droite)" },
    { value: "zoomInLeft", label: "Zoom In (gauche)" },
    { value: "slideInRight", label: "Slide In (droite)" },
    { value: "slideInLeft", label: "Slide In (gauche)" },
];
